import { BaseComponent } from '../../base-component';
import { guid } from '@otto-ec/global-resources/misc';
import { Action, DataContainer, Feature, FeatureStatus, submitMove } from '@otto-ec/tracking-bct';
import {
    Alternative,
    EnergyEfficiency,
    FormattedPrice,
    Recommendation,
    RecommendationResponse,
    Star,
    Topics,
} from './sponsored-content-alternative.component.types';
import { trackView } from '@otto-ec/nexus-registry/pdp/tracking';

export class SponsoredContentAlternativeComponent extends BaseComponent {
    public rootSelector = '.js_pdp_sponsored-content-alternative__outer-frame';
    private sponsoredContentAlternativeId: string = '';
    private sponsoredContentAlternativeLink: string = '';

    public async init(): Promise<void> {
        o_global.eventQBus.on(Topics.SPONSORED_CONTENT_ALTERNATIVE_REVEAL, () => {
            this.documentReady()
                .then(this.render.bind(this))
                .then(this.emitReady.bind(this))
                .then(this.track.bind(this))
                .catch(this.handleFailure.bind(this));
        });
    }

    protected override show() {
        this.getRootElement().classList.remove('oc-ft5-visually-hidden');
    }

    protected override hide() {
        this.getRootElement().classList.add('oc-ft5-visually-hidden');
    }

    private track() {
        trackView({}, this.createViewTrackingFeatures('loaded', 'SponsoredContentAlternative', 'SponsoredContentAlternative'));
    }

    private emitReady() {
        o_global.eventQBus.emit(Topics.SPONSORED_CONTENT_ALTERNATIVE_READY);
    }

    private handleFailure() {
        this.emitFailed();
    }

    private emitFailed() {
        o_global.eventQBus.emit(Topics.SPONSORED_CONTENT_ALTERNATIVE_FAILED);
    }

    private async documentReady(): Promise<void> {
        return new Promise(resolve => {
            if (document.readyState === 'complete' || document.readyState === 'interactive') {
                resolve();
            } else {
                document.addEventListener('DOMContentLoaded', () => {
                    resolve();
                });
            }
        });
    }

    private async render() {
        if (!this.isPresent()) {
            return;
        }
        const variationId = this.getVariationId();

        const sponsoredContentAlternative = await this.getAlternativeVariation(variationId);
        if (this.isEmpty(sponsoredContentAlternative)) {
            return;
        }

        const iconWrapper = this.getRootElement().querySelector('.js_pdp_sponsored-content-alternative__icon_wrapper');
        const alternativeTextElement = this.getRootElement().querySelector('.js_pdp_sponsored-content-alternative__header--text');
        const reviewsElement = this.getRootElement().querySelector('.js_pdp_sponsored-content-alternative__number-of-reviews');
        const linkElement = this.getRootElement().querySelector('.js_pdp_sponsored-content-alternative__border-frame');
        const imgElement = this.getRootElement().querySelector('.js_pdp_sponsored-content-alternative__image');
        const retailPriceElement = this.getRootElement().querySelector('.js_pdp_sponsored-content-alternative_retail-price');
        const pricePrefixElement = this.getRootElement().querySelector('.js_pdp_sponsored-content-alternative_strike-price-prefix');
        const originalPriceElement = this.getRootElement().querySelector('.js_pdp_sponsored-content-alternative_strike-price-value');
        const strikePriceWrapperElement = this.getRootElement().querySelector('.js_pdp_sponsored-content-alternative_strike-price');
        const unitPriceElement = this.getRootElement().querySelector('.js_pdp_sponsored-content-alternative_unit_price');
        const energyElement = this.getRootElement().querySelector('.js_pdp_sponsored-content-alternative__energy-wrapper');

        if (
            !sponsoredContentAlternative ||
            !iconWrapper ||
            !alternativeTextElement ||
            !reviewsElement ||
            !linkElement ||
            !imgElement ||
            !retailPriceElement ||
            !pricePrefixElement ||
            !originalPriceElement ||
            !unitPriceElement ||
            !strikePriceWrapperElement ||
            !energyElement
        ) {
            return;
        }

        imgElement.setAttribute('src', `${sponsoredContentAlternative.image}`);

        alternativeTextElement.textContent = `${sponsoredContentAlternative.brandName} ${sponsoredContentAlternative.name}`;
        if (this.hasReviews(sponsoredContentAlternative)) {
            iconWrapper.innerHTML = this.createRatingStars(sponsoredContentAlternative.averageRatingRounded)
                .map(star => `<oc-icon-v1 type="rating-${star}" size="50"></oc-icon-v1>`)
                .join('');
            reviewsElement.textContent = `(${sponsoredContentAlternative.numberOfReviews})`;
        }
        this.sponsoredContentAlternativeLink = sponsoredContentAlternative.link;
        linkElement.setAttribute('href', this.sponsoredContentAlternativeLink);

        const formattedPrice = sponsoredContentAlternative.formattedPrice;
        retailPriceElement.innerHTML = `${formattedPrice.retailPrice}`;

        this.buildOptionalElements({
            formattedPrice,
            energyEfficiency: sponsoredContentAlternative.energyEfficiencyClasses?.[0],
            pricePrefixElement,
            originalPriceElement,
            retailPriceElement,
            strikePriceWrapperElement,
            unitPriceElement,
            energyElement,
        });
        this.sponsoredContentAlternativeId = sponsoredContentAlternative.id;
        linkElement.addEventListener('click', this.clickHandler.bind(this), { once: true });
        this.show();
    }

    private clickHandler(event: Event) {
        event.preventDefault();
        const pdpVariationId = this.getRootElement().dataset.variationId!;
        submitMove(...this.createClickTrackingData(pdpVariationId));
        if (o_product?.debug) {
            return;
        }
        setTimeout(this.navigateToAlternative.bind(this), 1000);
    }

    private navigateToAlternative() {
        if (!this.sponsoredContentAlternativeLink) {
            this.logError({
                message: 'Sponsored content alternative link is not set',
                error: new Error('Sponsored content alternative link is not set'),
            });
        } else {
            window.location.href = this.sponsoredContentAlternativeLink;
        }
    }

    private hasReviews(alternative: Alternative) {
        return (
            alternative.averageRatingRounded &&
            alternative.numberOfReviews &&
            alternative.averageRatingRounded > 0 &&
            alternative.numberOfReviews > 0
        );
    }

    private buildOptionalElements(elements: {
        formattedPrice: FormattedPrice;
        energyEfficiency?: EnergyEfficiency;
        pricePrefixElement: Element;
        originalPriceElement: Element;
        retailPriceElement: Element;
        strikePriceWrapperElement: Element;
        unitPriceElement: Element;
        energyElement: Element;
    }) {
        const {
            formattedPrice,
            energyEfficiency,
            pricePrefixElement,
            originalPriceElement,
            retailPriceElement,
            strikePriceWrapperElement,
            unitPriceElement,
            energyElement,
        } = elements;

        if (formattedPrice.originalPrice) {
            pricePrefixElement.innerHTML = formattedPrice.originalPricePrefix ?? '';
            originalPriceElement.innerHTML = formattedPrice.originalPrice ?? '';
            retailPriceElement.classList.remove('pdp_sponsored-content-alternative__price--retail');
            retailPriceElement.classList.add('pdp_sponsored-content-alternative__price--retail--red');
            strikePriceWrapperElement.classList.remove('oc-ft5-visually-hidden');
        }

        if (formattedPrice.unitPrice) {
            unitPriceElement.innerHTML = formattedPrice.unitPrice ?? '';
            unitPriceElement.classList.remove('oc-ft5-visually-hidden');
        }

        if (energyEfficiency) {
            energyElement.innerHTML = `<oc-energy-label-v1 color="${energyEfficiency.colorHexCode}" 
                                        size="100" variant="${energyEfficiency.category}" 
                                        version="${energyEfficiency.uniform ? 'uniform' : 'default'}">                                      
                                        </oc-energy-label-v1>`;
            energyElement.classList.remove('oc-ft5-visually-hidden');
        }
    }

    private extractAlternative(response: RecommendationResponse) {
        return this.recommandationToAlternative(response.recommendations[0]);
    }

    private validateRecommendation(r: Recommendation) {
        return (
            r?.brandName &&
            r?._embedded?.['o:variation']?.name &&
            r?.image?._links?.external?.href &&
            r?.formattedPrice &&
            r?._embedded?.['o:variation']?._links?.['o:detail-view']?.href
        );
    }

    private recommandationToAlternative(r: Recommendation): Alternative {
        if (!this.validateRecommendation(r)) {
            throw new Error('Invalid recommendation');
        }
        return {
            name: r._embedded?.['o:variation'].name,
            id: r.meta.product_VariationId,
            brandName: r.brandName.toUpperCase(),
            averageRatingRounded: r._embedded?.['o:variation'].averageRatingRounded,
            numberOfReviews: r._embedded?.['o:variation'].numberOfReviews,
            image: r.image._links.external.href,
            link: r._embedded?.['o:variation']._links?.['o:detail-view'].href,
            formattedPrice: r.formattedPrice,
            energyEfficiencyClasses: r._embedded?.['o:variation'].energyEfficiencyClasses,
        };
    }

    private roundHalf = (num: number) => Math.round(num * 2) / 2;

    private createRatingStars(roundedAverageRating = 0): Star[] {
        const roundedRating = this.roundHalf(roundedAverageRating);

        const stars: Star[] = ['empty', 'empty', 'empty', 'empty', 'empty'];
        if (roundedAverageRating <= 0) return stars;

        const roundedIndex = Math.floor(roundedRating);
        stars.fill('filled', 0, roundedIndex);

        if (roundedRating % 1 !== 0) {
            stars[roundedIndex] = 'half';
        }
        return stars;
    }

    private async getAlternativeVariation(inputVariationId: string): Promise<Alternative | undefined> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);

        return fetch(`/reco-core/recommendations/alternative/variations/${inputVariationId}`, {
            headers: {
                'Content-Type': 'application/hal+json;profile="https:/*                                                     */
            },
            signal: controller.signal,
        })
            .then(response => response.json())
            .then(data => this.extractAlternative(data))
            .catch(err => {
                throw new Error('Invalid recommendation. variation id: ' + inputVariationId + ' ' + err);
            })
            .finally(() => clearTimeout(timeoutId));
    }

    private isEmpty(object: unknown | unknown[]): boolean {
        return !object || Object.keys(object).length === 0 || (Array.isArray(object) && object?.length === 0);
    }

    private createClickTrackingData(pdpVariationId: string): [DataContainer, Action] {
        const parentId = guid();
        const trackingName = 'SponsoredContentAlternative';

        return [
            {
                product_VariationId: [pdpVariationId],
            },
            {
                name: 'click',
                features: this.createViewTrackingFeatures('clicked', trackingName, parentId),
            },
        ];
    }

    private createViewTrackingFeatures(trackingType: FeatureStatus, trackingName: string, parentId: string): Feature[] {
        return [
            {
                id: parentId,
                name: trackingName,
                status: trackingType,
                position: 99,
                labels: {
                    promo_Feature: ['true'],
                },
            },
            {
                id: guid(),
                name: `${trackingName}-Product`,
                status: trackingType,
                position: 99,
                parentId: parentId,
                variationId: this.sponsoredContentAlternativeId,
                labels: {},
            },
        ];
    }
}
